import { PlayerDetails } from '../PlayerDetails/PlayerDetails';
import { ApplePayTabDetails } from './ApplePayTabDetails';
import { DetailsWrapper } from '../DetailsWrapper/DetailsWrapper';
import { PurchasedItems } from '../PurchasedItems/PurchasedItems';
import { useIsBottomSheet } from '@/context/BottomSheetContext';
import { useContext, useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsLandscape } from '@/hooks/useIsLandscape';
import { useLandscapeTransition } from '@/hooks/useLandscapeTransition';
import { isLandscape } from '@/utils/device';
import { isApplePayTabMode } from '@/utils/location';
import { useDetailsContentColor, useHasDetailsBackground } from '@/hooks/useDetailsContentColor';
import { CheckoutContext } from '@/context/CheckoutContext';
import { TotalPrice } from '../PriceDetails/TotalPrice';
import { PriceDetails } from '../PriceDetails/PriceDetails';
import { cn } from '@/utils/cn';
import { BLACK_COLOR, type TemplateFeatures } from '@/constants/templateConfig';
import { DETAILS_STYLES } from '@/constants/styles';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { mixpanelService } from '@/services/mixpanelService';
import { Divider } from '../Common/Divider';
import { PromoCode } from '../PromoCode/PromoCode';
import { PaymentMethodEnum } from '@/types/common';
import { TemplateType, type BootCheckoutConfiguration } from '@/types/boot';
import { shouldCollapseTotal, shouldExpandDetails } from '@/utils/conditionalRendering';
import { isMultiOfferCart } from '@/utils/offers';
import { SubscriptionDetails } from '../Subscriptions/SubscriptionDetails/SubscriptionDetails';

export const Details = ({
  landscapeFillHeight = true,
  defaultOpen,
}: { landscapeFillHeight?: boolean; defaultOpen?: boolean } = {}) => {
  // True only inside the swipe-up sheet (provided by PreferredPaymentDrawerView).
  const bottomSheet = useIsBottomSheet();
  const context = useContext(CheckoutContext);

  const { pricing, template, paymentMethodSelected, card, boot, featureFlags, offers, purchasedItems, subscription } =
    context;
  const features = template.features;
  const styles = template.styles;
  const compactDetailsLayout = features.compactDetailsLayout;
  // Apple Pay fallback tab (iOS <= 16): its own screen (see the return below),
  // where the whole order is laid out flat — so none of the collapse machinery
  // in this component applies to it.
  const applePayTabMode = isApplePayTabMode();
  // `checkout_expanded_details` (ACDEV-25284): publisher opt-in to start the
  // section expanded. Read here rather than inside the resolver because the
  // landscape-rotation handler below needs the same answer.
  const expandDetails = shouldExpandDetails(featureFlags);
  const [showPurchedItems, setShowPurchedItems] = useState(() =>
    resolveDetailsDefaultOpen({
      defaultOpen,
      expandDetails,
      features,
      templateType: template.type,
      paymentMethodSelected,
      savedCardsCount: card?.savedCards?.length ?? 0,
      checkoutConfiguration: boot?.checkoutConfiguration,
    })
  );
  // The initial state above only reads `isLandscape()` once at mount. When the
  // shopper rotates INTO landscape (FTD / last-payment / default templates),
  // collapse the breakdown so the price + CTA don't get pushed off the short
  // landscape viewport. Only fires on the portrait -> landscape transition, so
  // a manual expand while already in landscape is respected.
  // Skipped under `expandDetails`, which already overrides the mount-time
  // landscape collapse — letting rotation re-collapse would make the same
  // session land in two different states depending on how it got there.
  useLandscapeTransition({ onEnter: () => !expandDetails && setShowPurchedItems(false) });
  const isMobile = useIsMobile();
  // Reactive mirror of `isLandscape()` (the app's phone-landscape band). Gates
  // `landscapeFill` in `containerClassName` below so the CSS fill matches the
  // JS band exactly, rather than Tailwind's looser orientation media query.
  const isInLandscapeBand = useIsLandscape();
  // Multi-offer cart: `OffersPane` in `PlayerDetails` renders the offers UI,
  // so the single-offer `PurchasedItems` breakdown below is skipped. Both are
  // first-class variants — this is the multi-offer variant of a live cart.
  const isMultiOfferCartCheckout = isMultiOfferCart(offers);
  // `bottomSheet` (the white swipe-up sheet) opts OUT of the publisher-hero
  // contrast logic: that surface never paints the hero, so the auto-white
  // text/icons the hook would pick are invisible on white. Force black text
  // and treat the surface as background-less. The hooks still run
  // unconditionally (stable hook order).
  const detailsColorFromHook = useDetailsContentColor();
  const hasBackgroundFromHook = useHasDetailsBackground();
  const detailsColor = bottomSheet ? { color: BLACK_COLOR } : detailsColorFromHook;
  const hasBackground = bottomSheet ? false : hasBackgroundFromHook;

  const containerClassName = cn(
    DETAILS_STYLES.base,
    // Phone-landscape band: full fill + auto-margin centering (scroll-safe).
    landscapeFillHeight && isInLandscapeBand && DETAILS_STYLES.landscapeFill,
    // CSS-landscape-but-JS-portrait viewports: scroll shell with a top gap,
    // but no auto-margin centering. Preserves scroll access to Total when the
    // OffersPane expands (see `landscapeScroll`).
    landscapeFillHeight && !isInLandscapeBand && DETAILS_STYLES.landscapeScroll,
    styles.detailsContainer
  );
  const collapsibleClassName = cn(
    DETAILS_STYLES.collapsibleBase,
    showPurchedItems ? styles.detailsCollapsible.open : styles.detailsCollapsible.closed,
    'w-full'
  );

  const promoCodeRow =
    !bottomSheet && !isMobile ? (
      <div className="mt-2 w-full">
        <PromoCode />
      </div>
    ) : null;

  const wrapperClassName = isMobile ? styles.detailsWrapper.mobile : styles.detailsWrapper.desktop;

  const handleShowPurchasedItems = () => {
    setShowPurchedItems(!showPurchedItems);
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.CHECKOUT_SECTION_STATE_CHANGED, {
      ...(properties as Record<string, unknown>),
      type: showPurchedItems ? 'expanded' : 'collapsed',
    });
  };

  // `checkout_collapse_total`: fold the Total INTO the collapsible so a
  // collapsed header shows only the offer name + Details toggle (the total is
  // revealed on expand). Applies to every template. Off (default) keeps the
  // legacy always-visible Total row untouched.
  const collapseTotal = shouldCollapseTotal(featureFlags);

  // What collapses (Figma: a CLOSED Details shows ONLY the Total). The
  // products list and subtotal/tax live inside the collapsible:
  //   items  →  subtotal/tax        (hidden when closed)
  //   total                           (always visible, see below —
  //                                    unless `collapseTotal`, then it
  //                                    joins the collapsible here)
  //   promo                           (always visible, outside collapsible —
  //                                    ACDEV-26840)
  const collapsibleDetails = (
    <>
      {!isMultiOfferCartCheckout && <DetailsWrapper />}
      {promoCodeRow}
      <div className={cn('flex flex-col gap-3 w-full', !isMultiOfferCartCheckout && 'mt-3')}>
        <PriceDetails />
        {collapseTotal && <TotalPrice pricing={pricing} />}
      </div>
    </>
  );

  // Total is ALWAYS visible — even collapsed — so the price is never hidden.
  // Under `collapseTotal` it moves into the collapsible above, so this
  // always-on row is dropped.
  const totalRow = collapseTotal ? null : (
    <div className="mt-2 w-full">
      <TotalPrice pricing={pricing} />
    </div>
  );

  const renderContent = () => {
    // Swipe-up sheet (Sprint-90 Figma): a clean white card. The breakdown is
    // ordered totals-first — Subtotal/Tax reveal ABOVE the always-visible Total
    // on expand, then a background-less product list — fenced by a hairline
    // Divider ABOVE and BELOW — reveals under it. Both reveals ride the same
    // Details toggle via two collapsibles (open-state padding lives on an INNER
    // child so it clips to zero when closed — see the collapse gotcha below — no
    // phantom gap around the collapsed Total, and no stray line above pay). The
    // PromoCode field and the gray items box (DEFAULT/SINGLE chrome) are
    // dropped here — they read as busy on the minimal express sheet; a coupon
    // is still reachable via "Use Other Payment Method" (the DEFAULT flow).
    if (bottomSheet) {
      // Collapse gotcha: the reveal wrappers use the `grid-rows-[0fr]` trick,
      // which clamps the `overflow-hidden` child to 0 height — BUT a box with
      // `box-sizing: border-box; height: 0` can't shrink below its OWN padding,
      // so any padding on that clip wrapper survives the collapse and leaves a
      // phantom gap when closed. Keep the clip wrapper padding-free and put the
      // open-state spacing on an INNER child (which the clip then hides at 0).
      return (
        <div className="flex w-full flex-col">
          <div className={collapsibleClassName}>
            <div className={styles.detailsCollapsible.content}>
              <div className="pb-2">
                <PriceDetails />
              </div>
            </div>
          </div>
          <TotalPrice pricing={pricing} />
          <div className={collapsibleClassName}>
            <div className={styles.detailsCollapsible.content}>
              {/* `pt-3`/`pb-2` breathe only when open: `pt-3` sets the Total →
                  list gap, `pb-2` (plus the CTA's `pt-3` below) lifts the closing
                  hairline off the pay button. Because they live on this INNER
                  child, they clip to zero when closed — no phantom gap on the
                  collapsed Total→button card (its spacing is the CTA's `pt-3`). */}
              {purchasedItems.length > 0 && (
                <div className="flex flex-col gap-4 pt-3 pb-2">
                  <Divider />
                  <PurchasedItems bare />
                  {/* Closing hairline: on expand it fences the product list off
                    from the pay button below (Figma). */}
                  <Divider />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (compactDetailsLayout) {
      return (
        <div className="flex flex-col w-full pb-2">
          <div data-testid="one-payment-details-wrapper" className={collapsibleClassName}>
            <div className={styles.detailsCollapsible.content}>{collapsibleDetails}</div>
          </div>
          {totalRow}
          {/* Divider only when there is no publisher background — when a
              background image is rendered the image edge already provides
              the visual break between Details and the CTA. */}
          {!hasBackground && (
            <div className="flex flex-col w-full mt-3">
              <Divider />
            </div>
          )}
        </div>
      );
    }

    if (!isMobile) {
      return (
        <div className={wrapperClassName}>
          {collapsibleDetails}
          {totalRow}
        </div>
      );
    }

    // Multi-offer mobile: `OffersPane` renders the items UI AND drops the
    // `ItemsDetails` toggle in `PlayerDetails`, so nothing can open the
    // collapsible below. Render the breakdown directly — otherwise
    // PriceDetails stay clipped at 0 height for the whole session.
    if (isMultiOfferCartCheckout) {
      return (
        <div className="mb-3">
          <div data-testid="mobile-details-wrapper">{collapsibleDetails}</div>
          {totalRow}
          {promoCodeRow}
        </div>
      );
    }

    return (
      <div className="mb-3">
        <div data-testid="mobile-details-wrapper" className={collapsibleClassName}>
          <div className={styles.detailsCollapsible.content}>{collapsibleDetails}</div>
        </div>
        {totalRow}
        {promoCodeRow}
      </div>
    );
  };

  return (
    <div data-testid="details-section" className={containerClassName} style={detailsColor}>
      {/* The fallback tab has its own screen — see ApplePayTabDetails. None of
          the arms above apply there: there is nothing to collapse. Subscriptions
          have no items list to collapse either, and their own line item/total
          copy ("Total due today"), so the breakdown itself gets its own fixed,
          non-collapsible summary — see SubscriptionDetails. PlayerDetails (logo +
          plan name) still renders above it so the hero matches one-time
          purchase. */}
      {applePayTabMode ? (
        <ApplePayTabDetails />
      ) : subscription ? (
        <>
          <PlayerDetails
            showPurchedItems={showPurchedItems}
            handleShowPurchedItems={() => handleShowPurchasedItems()}
          />
          <SubscriptionDetails />
        </>
      ) : (
        <>
          <PlayerDetails
            showPurchedItems={showPurchedItems}
            handleShowPurchedItems={() => handleShowPurchasedItems()}
          />
          {renderContent()}
        </>
      )}
    </div>
  );
};

interface DetailsDefaultOpenInput {
  /** Host override — see precedence note below. */
  defaultOpen?: boolean;
  /** `checkout_expanded_details` (see {@link shouldExpandDetails}). */
  expandDetails: boolean;
  features: TemplateFeatures;
  templateType: TemplateType;
  paymentMethodSelected: PaymentMethodEnum;
  savedCardsCount: number;
  checkoutConfiguration?: BootCheckoutConfiguration;
}

/**
 * Whether the Details section starts EXPANDED, in strict precedence order:
 *
 *   1. `defaultOpen` — the host owns the surface. The swipe-up sheet passes
 *      `false` because its whole point is a compact one-tap card, and that
 *      must hold even for a publisher who opted into `checkout_expanded_details`
 *      (an expanded sheet defeats the design). Highest precedence, so hosts
 *      that pass nothing fall through to the flag.
 *   2. `checkout_expanded_details` — publisher opt-in (ACDEV-25284) restoring
 *      what v2 gave them via `checkout_mobile_collapsed_breakdown: false`.
 *      Beats the per-template default AND every collapse exception below.
 *   3. The per-template `defaultDetailsOpen`, minus the three exceptions.
 *
 * Pure and side-effect free apart from reading the viewport via
 * `isLandscape()`, so it's only ever called for the mount-time state.
 */
const resolveDetailsDefaultOpen = ({
  defaultOpen,
  expandDetails,
  features,
  templateType,
  paymentMethodSelected,
  savedCardsCount,
  checkoutConfiguration,
}: DetailsDefaultOpenInput): boolean => {
  if (defaultOpen !== undefined) return defaultOpen;
  if (expandDetails) return true;
  if (!features.defaultDetailsOpen) return false;

  // Landscape phones are short and wide: an open items list pushes the price
  // breakdown + CTA off-screen. The toggle — surfaced in landscape via
  // PlayerDetails — still lets the user expand.
  if (isLandscape()) return false;

  // SINGLE opens the breakdown by default, EXCEPT when the one method is a raw
  // credit-card form with no saved cards to pick from: that form already owns
  // most of the viewport, so auto-expanding the items on top of it makes the
  // compact card overflow. Wallet/APM methods (and cards that show a saved
  // card) keep the open-by-default behaviour.
  const isCardWithoutSavedCards = paymentMethodSelected === PaymentMethodEnum.CREDIT_CARD && savedCardsCount === 0;
  if (features.compactDetailsLayout && isCardWithoutSavedCards) return false;

  return !isPreferredLastCardBranch({ templateType, paymentMethodSelected, savedCardsCount, checkoutConfiguration });
};

/**
 * PREFERRED has two branches: FTD wallet and returning-user last method. Only
 * the returning-user CARD branch collapses, so the saved-card CTA stays visible
 * without scrolling; FTD Apple/Google keeps the default open state.
 */
const isPreferredLastCardBranch = ({
  templateType,
  paymentMethodSelected,
  savedCardsCount,
  checkoutConfiguration,
}: Pick<
  DetailsDefaultOpenInput,
  'templateType' | 'paymentMethodSelected' | 'savedCardsCount' | 'checkoutConfiguration'
>): boolean => {
  if (templateType !== TemplateType.PREFERRED_PAYMENT_METHOD) return false;

  const fromBoot =
    checkoutConfiguration?.isFirstTimeDepositor !== true &&
    checkoutConfiguration?.preferredPaymentMethod === PaymentMethodEnum.CREDIT_CARD;
  // No BE-supplied last method, but a saved card is present — treat it as the
  // returning-user card branch too.
  const savedCardFallback =
    !checkoutConfiguration?.preferredPaymentMethod &&
    paymentMethodSelected === PaymentMethodEnum.CREDIT_CARD &&
    savedCardsCount > 0;

  return fromBoot || savedCardFallback;
};
